import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { downloadBlob } from "@/lib/resumeUtils";
import { X, Download, Sparkles, RefreshCw, UploadCloud, Trash2, FileText, Printer, Mail, MessageCircle, FileDown, Package, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import ThinkingLoader from "@/components/ThinkingLoader";

const SECTIONS = [
  { key: "intro", labelKey: "prepSectionIntro", roman: "I" },
  { key: "mission", labelKey: "prepSectionMission", roman: "II" },
  { key: "activities", labelKey: "prepSectionActivities", roman: "III" },
  { key: "media", labelKey: "prepSectionMedia", roman: "IV" },
  { key: "cv_fit", labelKey: "prepSectionCvFit", roman: "V" },
  { key: "competencies", labelKey: "prepSectionCompetencies", roman: "VI" },
  { key: "pitch", labelKey: "prepSectionPitch", roman: "VII" },
  { key: "questions_to_ask", labelKey: "prepSectionQuestions", roman: "VIII" },
  { key: "talking_points", labelKey: "prepSectionTalking", roman: "IX" },
  { key: "closing", labelKey: "prepSectionClosing", roman: "X" },
];

function SectionBlock({ roman, title, content }) {
  if (!content || (Array.isArray(content) && content.length === 0)) return null;
  return (
    <section>
      <p className="ed-eyebrow">{roman} · Sectie</p>
      <h3 className="ed-h2 mt-1">{title}</h3>
      <div className="ed-rule w-16 my-3" />
      {Array.isArray(content) ? (
        <ul className="space-y-3">
          {content.map((item, i) => (
            <li key={i} className="font-serif text-base leading-relaxed text-neutral-800 flex gap-3">
              <span className="ed-num shrink-0 mt-1">{i + 1}.</span>
              <span className="whitespace-pre-wrap">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-serif text-base leading-relaxed text-neutral-800 whitespace-pre-wrap">{content}</p>
      )}
    </section>
  );
}

export default function InterviewPrepSheet({ job, onClose }) {
  const confirm = useConfirm();
  const { t, lang } = useT();
  const [prep, setPrep] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [prepLang, setPrepLang] = useState(null);
  const [cvSource, setCvSource] = useState("none");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobCv, setJobCv] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [includeCv, setIncludeCv] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [dossierBusy, setDossierBusy] = useState(null);
  const fileRef = useRef(null);

  const loadExisting = useCallback(async () => {
    if (!job) return;
    setLoading(true);
    try {
      const [prepRes, cvRes, dossierRes] = await Promise.all([
        api.get(`/jobs/${job.id}/interview-prep`),
        api.get(`/jobs/${job.id}/cv`),
        api.get(`/letters/by-job/${job.id}`).catch(() => ({ data: { present: false } })),
      ]);
      if (prepRes.data.prep) {
        setPrep(prepRes.data.prep);
        setGeneratedAt(prepRes.data.generated_at);
        setPrepLang(prepRes.data.lang || "nl");
      } else {
        setPrep(null);
        setPrepLang(null);
      }
      // cv_source is now always returned by the GET endpoint (with or
      // without an existing prep) so we can label the source correctly
      // BEFORE the user hits Genereer. Prevents the misleading
      // "GEEN CV — voeg er één toe" banner when a CV variant is
      // actually linked to the letter.
      setCvSource(prepRes.data.has_cv ? (prepRes.data.cv_source || "central") : (prepRes.data.cv_source || "none"));
      setJobCv(cvRes.data.present ? cvRes.data : null);
      setDossier(dossierRes.data.present ? dossierRes.data : null);
    } catch {
      setPrep(null);
    } finally {
      setLoading(false);
    }
  }, [job]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const generate = useCallback(async () => {
    if (!job) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/interview-prep?lang=${lang}`);
      setPrep(data.prep);
      setGeneratedAt(data.generated_at);
      setPrepLang(data.lang || lang);
      setCvSource(data.cv_source || (data.has_cv ? "central" : "none"));
      toast.success(t("prepGeneratedAt"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("prepFailed"));
    } finally {
      setGenerating(false);
    }
  }, [job, lang, t]);

  // Auto-regenerate when the UI language changes and stored prep is in another language
  useEffect(() => {
    if (!prep || !prepLang) return;
    if (prepLang === lang) return;
    if (generating) return;
    generate();
  }, [lang, prep, prepLang, generating, generate]);

  if (!job) return null;


  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("prepFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const pdfFilename = () => {
    const safe = (job.company || "job").replace(/[^A-Za-z0-9_-]+/g, "_");
    return `interview_prep_${safe}.pdf`;
  };

  const fetchPdfBlob = async () => {
    const res = await api.get(
      `/jobs/${job.id}/interview-prep.pdf${includeCv ? "?include_cv=1" : ""}`,
      { responseType: "blob" },
    );
    return new Blob([res.data], { type: "application/pdf" });
  };

  const printPdf = async () => {
    setSharing(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("load", () => {
          try { win.print(); } catch { /* ignore */ }
        });
      }
      // Revoke later to allow the tab to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("prepFailed"));
    } finally {
      setSharing(false);
    }
  };

  const shareVia = async (channel) => {
    setSharing(true);
    try {
      const blob = await fetchPdfBlob();
      const file = new File([blob], pdfFilename(), { type: "application/pdf" });
      const subject = t("prepEmailSubject", { company: job.company });
      const body = t("prepEmailBody", { company: job.company, job_title: job.job_title });
      const waText = t("prepWhatsappText", { company: job.company, job_title: job.job_title });

      // Prefer Web Share API when file sharing is supported (mobile)
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: subject,
          text: channel === "whatsapp" ? waText : body,
        });
        return;
      }

      // Desktop / unsupported fallback: download PDF + open channel deep link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);

      if (channel === "email") {
        const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
      } else if (channel === "whatsapp") {
        const wa = `https://wa.me/?text=${encodeURIComponent(waText)}`;
        window.open(wa, "_blank", "noopener,noreferrer");
      }
      toast.info(t("prepShareUnsupported"));
    } catch (err) {
      if (err?.name === "AbortError") return; // user cancelled share sheet
      toast.error(err?.response?.data?.detail || t("prepFailed"));
    } finally {
      setSharing(false);
    }
  };

  const uploadJobCv = async (file) => {
    if (!file) return;
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/${job.id}/cv`, fd);
      setJobCv({ ...data, present: true });
      toast.success(t("cvUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("cvUploadFailed"));
    } finally {
      setUploadingCv(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteJobCv = async () => {
    if (!(await confirm(t("cvConfirmDelete")))) return;
    try {
      await api.delete(`/jobs/${job.id}/cv`);
      setJobCv(null);
      toast.success(t("cvDeleted"));
    } catch {
      toast.error("Failed");
    }
  };

  const downloadDossier = async (kind) => {
    if (!dossier?.letter) return;
    setDossierBusy(kind);
    try {
      const l = dossier.letter;
      const safe = (l.company || "job").replace(/[^A-Za-z0-9_-]+/g, "_");
      if (kind === "cv") {
        // letter_id → CV title auto-aligns with the vacancy on this render.
        const res = await api.get(`/resumes/${l.resume_id}/pdf?lang=${l.lang}&letter_id=${l.id}`, { responseType: "blob" });
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `cv_${safe}_${l.lang}.pdf`);
      } else if (kind === "letter") {
        const res = await api.get(`/letters/${l.id}/pdf?doc=letter`, { responseType: "blob" });
        const prefix = l.lang === "nl" ? "motivatiebrief" : "motivation_letter";
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `${prefix}_${safe}.pdf`);
      } else {
        const res = await api.get(`/letters/${l.id}/package`, { responseType: "blob" });
        const prefix = l.lang === "nl" ? "sollicitatiepakket" : "application_package";
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), `${prefix}_${safe}.pdf`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbPdfFailed"));
    } finally {
      setDossierBusy(null);
    }
  };

  const sourceLabel =
    cvSource === "job" ? t("prepUsingJobCv") :
    (cvSource === "central" || cvSource === "central_variant") ? t("prepUsingCentralCv") :
    t("prepNoCv");

  return (
    <div data-testid="prep-sheet">
      <div className="ed-sheet-overlay" onClick={onClose} />
      <div className="ed-sheet">
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-neutral-200 px-8 py-5 flex items-start justify-between z-10">
          <div className="max-w-[70%]">
            <p className="ed-eyebrow">{t("prepTitle")}</p>
            <h2 className="ed-h2 mt-1 leading-tight">{job.job_title}</h2>
            <p className="font-serif text-lg text-neutral-700 italic mt-1">
              {t("prepFor")} {job.company}{job.location ? ` · ${job.location}` : ""}
            </p>
          </div>
          <button
            data-testid="prep-close"
            onClick={onClose}
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-8 space-y-8">
          {/* Action bar */}
          <div className="ed-card p-5 space-y-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="ed-eyebrow">{sourceLabel}</p>
                {generatedAt && (
                  <p className="font-mono text-xs text-neutral-500 mt-1">
                    {t("prepGeneratedAt")}: {new Date(generatedAt).toLocaleString()}
                  </p>
                )}
              </div>
              <button
                data-testid="prep-generate-button"
                onClick={generate}
                disabled={generating}
                className="ed-btn"
              >
                {prep ? <RefreshCw size={14} strokeWidth={1.5} /> : <Sparkles size={14} strokeWidth={1.5} />}
                {generating ? t("prepGenerating") : (prep ? t("prepRegenerate") : t("prepGenerate"))}
              </button>
            </div>
            {generating && (
              <div className="mt-3" data-testid="prep-thinking-wrapper">
                <ThinkingLoader stage="prep" testId="prep-thinking" />
              </div>
            )}

            {prep && (
              <>
                <div className="border-t border-neutral-200 pt-4">
                  <label className="inline-flex items-start gap-3 cursor-pointer" data-testid="prep-include-cv-label">
                    <input
                      type="checkbox"
                      data-testid="prep-include-cv-checkbox"
                      checked={includeCv}
                      onChange={(e) => setIncludeCv(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-neutral-900"
                    />
                    <span>
                      <span className="ed-eyebrow block">{t("prepIncludeCv")}</span>
                      <span className="font-mono text-[0.7rem] text-neutral-500 mt-1 block">{t("prepIncludeCvHint")}</span>
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    data-testid="prep-download-pdf"
                    onClick={downloadPdf}
                    disabled={downloading || sharing}
                    className="ed-btn"
                  >
                    <Download size={14} strokeWidth={1.5} />
                    {downloading ? t("prepDownloading") : t("prepDownloadPdf")}
                  </button>
                  <button
                    data-testid="prep-print-button"
                    onClick={printPdf}
                    disabled={downloading || sharing}
                    className="ed-btn ed-btn-outline"
                  >
                    <Printer size={14} strokeWidth={1.5} />
                    {t("prepPrint")}
                  </button>
                  <button
                    data-testid="prep-email-button"
                    onClick={() => shareVia("email")}
                    disabled={downloading || sharing}
                    className="ed-btn ed-btn-outline"
                  >
                    <Mail size={14} strokeWidth={1.5} />
                    {t("prepEmail")}
                  </button>
                  <button
                    data-testid="prep-whatsapp-button"
                    onClick={() => shareVia("whatsapp")}
                    disabled={downloading || sharing}
                    className="ed-btn ed-btn-outline"
                  >
                    <MessageCircle size={14} strokeWidth={1.5} />
                    {t("prepWhatsapp")}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Application dossier — CV + letter used for this application */}
          <div className="ed-card p-5" data-testid="prep-dossier">
            <p className="ed-eyebrow flex items-center gap-2 mb-3">
              <ClipboardList size={12} strokeWidth={1.5} /> {t("prepDossier")}
            </p>
            {dossier?.letter ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-x-8 gap-y-2">
                  <div className="min-w-0">
                    <p className="ed-label mb-0.5">{t("prepDossierCv")}</p>
                    <p className="font-serif text-base text-neutral-800 truncate" data-testid="prep-dossier-cv-name">
                      {dossier.letter.resume_name || "—"}
                      {!dossier.resume_exists && <span className="font-mono text-[0.6rem] text-neutral-500 ml-2">({t("prepDossierCvGone")})</span>}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="ed-label mb-0.5">{t("prepDossierLetter")}</p>
                    <p className="font-serif text-base text-neutral-800 truncate" data-testid="prep-dossier-letter-name">
                      {dossier.letter.lang.toUpperCase()} · {new Date(dossier.letter.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {dossier.resume_exists && (
                    <button data-testid="prep-dossier-dl-cv" className="ed-btn ed-btn-outline" onClick={() => downloadDossier("cv")} disabled={!!dossierBusy}>
                      <FileDown size={14} strokeWidth={1.5} /> {dossierBusy === "cv" ? t("ltDlBusy") : t("prepDlCv")}
                    </button>
                  )}
                  <button data-testid="prep-dossier-dl-letter" className="ed-btn ed-btn-outline" onClick={() => downloadDossier("letter")} disabled={!!dossierBusy}>
                    <FileDown size={14} strokeWidth={1.5} /> {dossierBusy === "letter" ? t("ltDlBusy") : t("prepDlLetter")}
                  </button>
                  <button data-testid="prep-dossier-dl-package" className="ed-btn ed-btn-outline" onClick={() => downloadDossier("package")} disabled={!!dossierBusy}>
                    <Package size={14} strokeWidth={1.5} /> {dossierBusy === "package" ? t("ltDlBusy") : t("prepDlPackage")}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-500 italic font-serif" data-testid="prep-dossier-none">{t("prepDossierNone")}</p>
            )}
          </div>

          {/* Job-specific CV */}
          <div className="ed-card p-5">
            <p className="ed-eyebrow flex items-center gap-2 mb-3">
              <FileText size={12} strokeWidth={1.5} /> {t("prepUploadJobCv")}
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {jobCv ? (
                <p className="font-serif text-base text-neutral-800 min-w-0 truncate">{jobCv.filename}</p>
              ) : (
                <p className="text-sm text-neutral-500 italic font-serif">{t("cvNone")}</p>
              )}
              <div className="flex gap-2 shrink-0">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  data-testid="prep-job-cv-input"
                  onChange={(e) => uploadJobCv(e.target.files?.[0])}
                />
                <button
                  type="button"
                  data-testid="prep-job-cv-upload"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingCv}
                  className="ed-btn ed-btn-outline"
                >
                  <UploadCloud size={14} strokeWidth={1.5} />
                  {uploadingCv ? t("cvUploading") : (jobCv ? t("cvReplace") : t("cvUpload"))}
                </button>
                {jobCv && (
                  <button
                    type="button"
                    data-testid="prep-job-cv-delete"
                    onClick={deleteJobCv}
                    className="ed-btn ed-btn-outline"
                    title={t("cvRemove")}
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-neutral-500 font-mono mt-3">{t("cvHint")}</p>
          </div>

          {/* Prep content */}
          {loading ? (
            <div className="ed-card p-10 text-center">
              <p className="ed-eyebrow">{t("loading")}</p>
            </div>
          ) : !prep ? (
            <div className="ed-card p-10 text-center" data-testid="prep-none">
              <p className="text-sm text-neutral-500 italic font-serif">{t("prepNone")}</p>
            </div>
          ) : (
            <div className="space-y-10" data-testid="prep-content">
              {prepLang && prepLang !== lang && (
                <div className="ed-card p-4 border-l-4 border-neutral-900" data-testid="prep-lang-mismatch">
                  <p className="font-serif text-sm italic text-neutral-700">
                    {t("prepLangMismatch", {
                      stored: prepLang === "en" ? "English" : "Nederlands",
                      current: lang === "en" ? "English" : "Nederlands",
                    })}
                  </p>
                </div>
              )}
              <p className="ed-eyebrow text-center">{t("prepShareHint")}</p>
              {SECTIONS.map((s) => (
                <SectionBlock
                  key={s.key}
                  roman={s.roman}
                  title={t(s.labelKey)}
                  content={prep[s.key]}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
